// apps/pwa-pos/src/components/UserBadge.tsx
// MIB-48 — Nombre del cajero en el header.
//
// Pildora que muestra el nombre (o email fallback) del operador
// actual. Se renderiza en la zona "Estado" del header de SalePage.
// No rompe si la sesión está vacía (LoginGate cubre ese caso, pero
// defendedemos igual por si la primera lectura llega tarde).
//
// Truncado a 24 chars con ellipsis para evitar saltos de línea en
// mobile. Aria-label incluye rol cuando está disponible.

import { User } from 'lucide-react';
import type { UserSession } from '@/lib/auth';

const MAX_NAME = 24;

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  cajero: 'Cajero',
  supervisor: 'Supervisor',
  vendedor: 'Vendedor',
};

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}

function roleLabel(rol: string | undefined): string | null {
  if (!rol?.trim()) return null;
  const key = rol.trim().toLowerCase();
  return ROLE_LABELS[key] ?? rol.trim();
}

export function UserBadge({
  user,
  empresa,
  bodega,
}: {
  user: UserSession | null | undefined;
  empresa?: string | null;
  bodega?: string | null;
}) {
  if (!user) return null;
  const display = user.nombre?.trim() || user.email || '';
  if (!display) return null;
  const truncated = truncate(display, MAX_NAME);
  const rol = roleLabel(user.rol);
  const empresaText = empresa?.trim() || '…';
  const bodegaText = bodega?.trim() || '…';
  const ariaLabel = [
    `Usuario: ${display}`,
    rol ? `rol ${rol}` : null,
    `Empresa: ${empresaText}`,
    `Bodega: ${bodegaText}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex min-w-0 items-start gap-1.5" aria-label={ariaLabel} title={ariaLabel}>
      <User className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
      <div className="min-w-0 leading-tight">
        <p className="truncate text-sm font-semibold text-neutral-900">
          <span className="mr-1 text-xs font-medium text-neutral-500">Usuario</span>
          {truncated}
        </p>
        <p className="text-xs text-balance text-neutral-700">
          <span className="mr-1 font-medium text-neutral-500">Empresa</span>
          <span className="font-semibold text-neutral-900">{empresaText}</span>
          <span className="mx-1.5 text-neutral-400" aria-hidden="true">
            ·
          </span>
          <span className="mr-1 font-medium text-neutral-500">Bodega</span>
          <span className="font-semibold text-neutral-900">{bodegaText}</span>
        </p>
      </div>
    </div>
  );
}
